import IconButton from 'components/IconButton';
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import React, { useState } from 'react';
import Select from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import { toast, ToastContainer } from 'react-toastify';
import {
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { caRefreshAtom, appliedCAIdsAtom } from '../../CADashboard/state';
import { epRefreshAtom, appliedEPIdsAtom } from '../../EPDashboard/state';
import { appliedRowIdsAtom, sastRefreshAtom } from '../../SASTDashboard2/state';
import { appliedWAIdsAtom, waRefreshAtom } from '../../WADashboard/state';

interface Props {
  sourceType: string;
  qid: number;
  onClose: () => void;
}

const IgnoreReasons = [
  { value: 'FALSE_POSITIVE', label: 'False Positive' },
  { value: 'RISK_ACCEPTED', label: 'Risk Accepted' },
  { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
];

const VulnIgnoreModal = ({ qid, sourceType, onClose }: Props) => {
  const companyId = useAtomValue(companyIdAtom);

  const caHosts = useAtomValue(appliedCAIdsAtom);
  const setCaRrefresh = useSetAtom(caRefreshAtom);

  const epHosts = useAtomValue(appliedEPIdsAtom);
  const setEpRrefresh = useSetAtom(epRefreshAtom);

  const waHosts = useAtomValue(appliedWAIdsAtom);
  const setWaRrefresh = useSetAtom(waRefreshAtom);

  const sastHosts = useAtomValue(appliedRowIdsAtom);
  const setSastRrefresh = useSetAtom(sastRefreshAtom);

  const [ignoredReason, setIgnoredReason] = useState({
    value: 'FALSE_POSITIVE',
    label: 'False Positive',
  });
  const [ignoredComments, setIgnoredComments] = useState('');
  const [loading, setLoading] = useState(false);

  async function ignoreVuln() {
    const url = `findings/ignore`;

    let source_type_param = sourceType;
    let host_ids: number[] = [];
    if (
      sourceType === 'endpoint_data_external' ||
      sourceType === 'endpoint_data_internal' ||
      sourceType === 'endpoint_data_ec2' ||
      sourceType === 'endpoint_data'
    ) {
      // for external and internal endpoints, the  vulnerability groups will be different,
      //  so the qid is enough to identify the group
      // also the sourceType values for external and internal do not exists
      source_type_param = 'endpoint_data';
      host_ids = epHosts;
    } else if (sourceType === 'ca_data') {
      host_ids = caHosts;
      source_type_param = 'ca_data_server';
    } else if (sourceType === 'ca_data-workstations') {
      source_type_param = 'ca_data_workstation';
      host_ids = caHosts;
    } else if (sourceType === 'ca_data-all') {
      source_type_param = 'ca_data';
      host_ids = caHosts;
    } else if (sourceType === 'was_data') {
      host_ids = waHosts;
    } else if (sourceType === 'sast2') {
      source_type_param = 'sast2';
      host_ids = sastHosts;
    }

    const data = {
      action: 'ignore_all',
      qid: qid,
      company_id: companyId,
      reason: ignoredReason.value,
      comments: `Bulk Ignored from Dashboard with comment: ${ignoredComments}`,
      source_type: source_type_param,
    };
    if (host_ids.length > 0) {
      data['host_ids'] = host_ids;
    }
    setLoading(true);
    await callApi(url, 'PUT', {}, data)
      .then((res) => {
        if (res.status === 201 || res.status === 200) {
          // success
          if (
            sourceType === 'endpoint_data_external' ||
            sourceType === 'endpoint_data_internal' ||
            sourceType === 'endpoint_data_ec2' ||
            sourceType === 'endpoint_data'
          ) {
            setEpRrefresh(Math.random().toString());
          } else if (sourceType === 'was_data') {
            setWaRrefresh(Math.random().toString());
          } else if (
            sourceType === 'ca_data' ||
            sourceType === 'ca_data-workstations' ||
            sourceType === 'ca_data-all'
          ) {
            setCaRrefresh(Math.random().toString());
          } else if (sourceType === 'sast2') {
            setSastRrefresh(Math.random().toString());
          }
        } else {
          console.error({
            function: 'ignoreVuln',
            error: `Unexpected response: ${res.status}`,
            res,
          });
        }
        toast.info('Success! ' + res.data.message, {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      })
      .catch((err) => {
        console.log(err);
        const res = err.response;
        if (res.status === 422) {
          console.log(res.data.message);
        }
      })
      .finally(() => setLoading(false));
    onClose();
  }
  const SpinnerButton = (
    <CircleSpinner size={20} color="#0665a9" loading={true} />
  );
  return (
    <Modal isOpen={true} size="lg" zIndex={10000}>
      <ModalHeader toggle={onClose}>Ignore Vulnerability</ModalHeader>
      <ModalBody>
        <Row style={{ marginBottom: '15px' }}>
          <Col md="12" className="spaced">
            {(sourceType.includes('ca_data') ||
              sourceType.includes('endpoint_data')) && (
              <label
                className="text-warning form-check-label"
                htmlFor="vuln_ignore_options1"
              >
                <strong>Warning:</strong> There might be multiple
                vulnerabilities with the same QID. By bulk Ignoring one of these
                will result in all being ignored, including some that might be
                in Fixed status.
              </label>
            )}
          </Col>
          <Col md="12" className="spaced">
            <label style={{ marginBottom: '0px' }}>
              <p>Ignore Reason</p>
            </label>
          </Col>
          <Col md="12" className="spaced" style={{ marginBottom: '10px' }}>
            <Select
              className="custom-360-dropdown"
              value={ignoredReason}
              onChange={(e) => {
                if (e) {
                  console.log(e);
                  setIgnoredReason(e);
                }
              }}
              options={IgnoreReasons}
            />
          </Col>
          <Col md="12" className="spaced">
            <label style={{ marginBottom: '0px' }}>
              <p>Ignore Comments</p>
            </label>
          </Col>
          <Col md="12" className="spaced">
            <Input
              type="text"
              value={ignoredComments}
              placeholder="Comments"
              maxLength={100}
              onChange={(e) => {
                console.log(e);
                setIgnoredComments(e.target.value);
              }}
            />
          </Col>
          <Col md="12" className="spaced">
            <label className="text-info">
              Note: All instances of this vulnerability will be ignored and
              removed from the display of open vulnerabilities. They can be
              retrieved with the “Status” filter on the Vulnerabilities page.
              They can also be Unignored.
            </label>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          size="sm"
          onClick={() => {
            onClose();
            setIgnoredComments('');
          }}
        >
          Cancel
        </Button>
        {loading ? (
          <IconButton
            id="spinner"
            size="sm"
            onClick={() => {}}
            iconElement={SpinnerButton}
          />
        ) : (
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              void ignoreVuln();
            }}
          >
            Ignore
          </Button>
        )}
      </ModalFooter>
      <ToastContainer />
    </Modal>
  );
};

export default VulnIgnoreModal;
